import { z } from 'zod';

export const problemDetailSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  code: z.string().optional(),
  errors: z
    .array(
      z.object({
        field: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
});

export type ProblemDetail = z.infer<typeof problemDetailSchema>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly problem: ProblemDetail;
  readonly fieldErrors: Record<string, string>;

  constructor(problem: ProblemDetail, fallback: { status: number; message: string }) {
    super(problem.detail ?? problem.title ?? fallback.message);
    this.name = 'ApiError';
    this.status = problem.status ?? fallback.status;
    this.code = problem.code;
    this.problem = problem;
    this.fieldErrors = (problem.errors ?? []).reduce<Record<string, string>>((acc, e) => {
      acc[e.field] = e.message;
      return acc;
    }, {});
  }

  isCode(code: string): boolean {
    return this.code === code;
  }
}

export async function parseProblemDetail(response: Response): Promise<ApiError> {
  let problem: ProblemDetail = {};
  try {
    const json = await response.clone().json();
    const parsed = problemDetailSchema.safeParse(json);
    if (parsed.success) problem = parsed.data;
  } catch {
    /* body is not JSON — fall back to status text */
  }
  return new ApiError(problem, {
    status: response.status,
    message: response.statusText || 'Request failed',
  });
}
